import { isSignedOrder } from "@/domain/quant/signed-order";
import { orderRevertEnabled } from "@/shared/config/flags";
import type { OrderRole } from "@/features/order-instructions/lib/order-role";
import type { StageList } from "@/features/order-instructions/lib/stage-list";
import {
  ORDER_TABS,
  type OrderFilter,
  type OrderRow,
  type OrderTab,
  tabOfRow,
} from "@/features/order-instructions/lib/order-stages";

export type OrderColumn =
  | "client"
  | "portfolio"
  | "advisor"
  | "created"
  | "approved"
  | "quantified"
  | "executed"
  | "instruction"
  | "prices";

export const COLUMNS_OF_TAB: Record<OrderTab, readonly OrderColumn[]> = {
  signed: ["client", "portfolio", "advisor", "created", "approved", "instruction", "prices"],
  exec: ["client", "portfolio", "advisor", "created", "approved", "quantified", "instruction"],
  done: ["client", "portfolio", "advisor", "created", "approved", "quantified", "executed"],
};

export function bookOf(list: Pick<StageList, "own">, role: OrderRole): OrderFilter {
  return { advisorId: role === "advisor" ? (list.own?.advisorId ?? "") : "", query: "" };
}

export function countsByTab(
  rows: readonly OrderRow[],
  filter: Pick<OrderFilter, "advisorId">,
): Record<OrderTab, number> {
  const counts = Object.fromEntries(ORDER_TABS.map((tab) => [tab, 0])) as Record<OrderTab, number>;
  for (const row of rows) {
    const tab = tabOfRow(row);
    if (tab !== null && (filter.advisorId === "" || row.advisorId === filter.advisorId)) {
      counts[tab] += 1;
    }
  }
  return counts;
}

export type RowMenuAction =
  | "view"
  | "presentation"
  | "instruction"
  | "orderList"
  | "portfolio"
  | "revert";

export function rowMenuActions(row: OrderRow, role: OrderRole | null): RowMenuAction[] {
  const stage = tabOfRow(row);
  const actions: RowMenuAction[] = ["view"];
  if (row.presentationFileId) actions.push("presentation");
  if (row.instruction) actions.push("instruction");
  if (stage === "exec" || stage === "done") actions.push("orderList");
  if (stage === "done" && row.subjectId) actions.push("portfolio");
  if (stage === "exec" && role === "backOffice" && revertible(row)) actions.push("revert");
  return actions;
}

export function revertible(row: Pick<OrderRow, "instruction">): boolean {
  return (
    orderRevertEnabled() && isSignedOrder({ status: "APPROVED", instruction: row.instruction })
  );
}

export function pricesUpdated(row: OrderRow): boolean {
  return quantifiedSinceApproval(row, row.pricedAt);
}

export function quantifiedSinceApproval(row: OrderRow, quantifiedAt: string | null): boolean {
  const at = instant(quantifiedAt);
  const since = Math.max(instant(row.approvedAt) ?? 0, instant(row.inExecutionAt) ?? 0);
  return row.status === "APPROVED" && at !== null && at > since;
}

export function quantifiedFresh(
  row: OrderRow,
  quantifiedAt: string | null,
  now: number = Date.now(),
): boolean {
  const at = instant(quantifiedAt);
  return quantifiedSinceApproval(row, quantifiedAt) && at !== null && now - at < FRESH_PRICES_MS;
}

export function movedBack(row: OrderRow): boolean {
  return row.status === "APPROVED" && row.inExecutionAt !== null;
}

export function instant(value: string | null): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export const FRESH_PRICES_MS = 60 * 60_000;
